import { useState } from "react";

import { Add, Delete, Save } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  BulletinTypeResource,
  createBulletinType,
  deleteBulletinType,
  getBulletinTypes,
  patchBulletinType,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  BULLETIN_PRIORITIES,
  BULLETIN_PRIORITY_LABELS,
  BulletinPriority,
  getBulletinPriorityColor,
} from "@/components/alerts/constants";

type PriorityValue = BulletinPriority | "";

type RowEdit = { en?: string; fr?: string; priority?: PriorityValue };

type NewRow = {
  reference_name: string;
  en: string;
  fr: string;
  priority: PriorityValue;
};

const EMPTY_NEW: NewRow = { reference_name: "", en: "", fr: "", priority: "" };

export default function AlertBulletinTypeSettings() {
  const queryClient = useQueryClient();

  const { data: types = [], isLoading } = useQuery({
    queryKey: ["admin-bulletin-types"],
    queryFn: async () => {
      const res = await getBulletinTypes();
      return res.data ?? [];
    },
  });

  const [edits, setEdits] = useState<Record<number, RowEdit>>({});
  const [saving, setSaving] = useState<Record<number, boolean>>({});
  const [deleteTarget, setDeleteTarget] = useState<BulletinTypeResource | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [newRow, setNewRow] = useState<NewRow | null>(null);
  const [creating, setCreating] = useState(false);

  const getEdit = (
    row: BulletinTypeResource,
  ): { en: string; fr: string; priority: PriorityValue } => ({
    en: edits[row.id]?.en ?? row.en,
    fr: edits[row.id]?.fr ?? row.fr,
    priority: edits[row.id]?.priority ?? row.priority ?? "",
  });

  const handleChange = <K extends keyof RowEdit>(
    id: number,
    field: K,
    value: NonNullable<RowEdit[K]>,
  ) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const handleSave = async (row: BulletinTypeResource) => {
    const e = edits[row.id];
    if (!e) return;
    const body: { en?: string; fr?: string; priority?: BulletinPriority } = {};
    if (e.en !== undefined && e.en !== row.en) body.en = e.en;
    if (e.fr !== undefined && e.fr !== row.fr) body.fr = e.fr;
    if (
      e.priority !== undefined &&
      e.priority !== "" &&
      e.priority !== row.priority
    ) {
      body.priority = e.priority;
    }
    if (!Object.keys(body).length) return;

    setSaving((prev) => ({ ...prev, [row.id]: true }));
    try {
      await patchBulletinType({ path: { type_id: row.id }, body });
      await queryClient.invalidateQueries({
        queryKey: ["admin-bulletin-types"],
      });
      setEdits((prev) => {
        const n = { ...prev };
        delete n[row.id];
        return n;
      });
      toast.success(`Saved "${row.reference_name}"`);
    } catch {
      toast.error(`Failed to save "${row.reference_name}"`);
    } finally {
      setSaving((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteBulletinType({ path: { type_id: deleteTarget.id } });
      await queryClient.invalidateQueries({
        queryKey: ["admin-bulletin-types"],
      });
      toast.success(`Deleted "${deleteTarget.reference_name}"`);
      setDeleteTarget(null);
    } catch {
      toast.error(`Failed to delete "${deleteTarget.reference_name}"`);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreate = async () => {
    if (!newRow || !newRow.reference_name || !newRow.en || !newRow.fr) {
      toast.error("All fields are required");
      return;
    }
    setCreating(true);
    try {
      const { priority, ...rest } = newRow;
      await createBulletinType({
        body: { ...rest, ...(priority ? { priority } : {}) },
      });
      await queryClient.invalidateQueries({
        queryKey: ["admin-bulletin-types"],
      });
      toast.success(`Created "${newRow.reference_name}"`);
      setNewRow(null);
    } catch {
      toast.error(`Failed to create "${newRow.reference_name}"`);
    } finally {
      setCreating(false);
    }
  };

  const isDirty = (row: BulletinTypeResource) => {
    const e = edits[row.id];
    if (!e) return false;
    const enChanged = e.en !== undefined && e.en !== row.en;
    const frChanged = e.fr !== undefined && e.fr !== row.fr;
    const priorityChanged =
      e.priority !== undefined &&
      e.priority !== "" &&
      e.priority !== row.priority;
    return enChanged || frChanged || priorityChanged;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" fontWeight={600}>
          Alert Types
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setNewRow(EMPTY_NEW)}
        >
          Add Alert Type
        </Button>
      </Box>

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 220, color: "text.secondary" }}>
                  Reference Name
                </TableCell>
                <TableCell sx={{ color: "text.secondary" }}>English</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>French</TableCell>
                <TableCell sx={{ width: 160, color: "text.secondary" }}>
                  Priority
                </TableCell>
                <TableCell sx={{ width: 96 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {types.map((row) => {
                const edit = getEdit(row);
                return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontFamily="monospace"
                      >
                        {row.reference_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={edit.en}
                        onChange={(e) =>
                          handleChange(row.id, "en", e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={edit.fr}
                        onChange={(e) =>
                          handleChange(row.id, "fr", e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={edit.priority}
                        onChange={(e) =>
                          handleChange(
                            row.id,
                            "priority",
                            e.target.value as PriorityValue,
                          )
                        }
                        SelectProps={{
                          renderValue: (value) => {
                            const v = value as PriorityValue;
                            if (!v) return <em>None</em>;
                            return (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                  sx={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: "50%",
                                    background: getBulletinPriorityColor(v),
                                    border: "1px solid rgba(0,0,0,0.1)",
                                  }}
                                />
                                {BULLETIN_PRIORITY_LABELS[v]}
                              </Box>
                            );
                          },
                        }}
                      >
                        {BULLETIN_PRIORITIES.map((p) => (
                          <MenuItem key={p} value={p}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box
                                sx={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: "50%",
                                  background: getBulletinPriorityColor(p),
                                  border: "1px solid rgba(0,0,0,0.1)",
                                }}
                              />
                              {BULLETIN_PRIORITY_LABELS[p]}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Save changes">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={!isDirty(row) || saving[row.id]}
                              onClick={() => handleSave(row)}
                            >
                              {saving[row.id] ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Save fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteTarget(row)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create dialog */}
      <Dialog
        open={!!newRow}
        onClose={() => setNewRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Alert Type</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Reference name (snake_case)"
              size="small"
              fullWidth
              value={newRow?.reference_name ?? ""}
              onChange={(e) =>
                setNewRow((p) => p && { ...p, reference_name: e.target.value })
              }
              placeholder="e.g. warrant_of_arrest"
            />
            <TextField
              label="English name"
              size="small"
              fullWidth
              value={newRow?.en ?? ""}
              onChange={(e) =>
                setNewRow((p) => p && { ...p, en: e.target.value })
              }
            />
            <TextField
              label="French name"
              size="small"
              fullWidth
              value={newRow?.fr ?? ""}
              onChange={(e) =>
                setNewRow((p) => p && { ...p, fr: e.target.value })
              }
            />
            <TextField
              select
              label="Priority"
              size="small"
              fullWidth
              value={newRow?.priority ?? ""}
              onChange={(e) =>
                setNewRow(
                  (p) =>
                    p && { ...p, priority: e.target.value as PriorityValue },
                )
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {BULLETIN_PRIORITIES.map((p) => (
                <MenuItem key={p} value={p}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        background: getBulletinPriorityColor(p),
                        border: "1px solid rgba(0,0,0,0.1)",
                      }}
                    />
                    {BULLETIN_PRIORITY_LABELS[p]}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewRow(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? <CircularProgress size={18} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Alert Type</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteTarget?.reference_name}</strong>? This cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
